import { runInAction } from 'mobx';
import { matchPath } from 'react-router-dom';

import { BasePathItems, BasePath, pagePathToPathString } from 'services/base/page_path';

import { FCMMessageKind, FCMMessage } from 'services/base/ipc_types';

import { Pangolin } from 'services/accounts/types';
import { GlobalAccountsDomain } from 'services/accounts/accounts_domain';

import { GlobalPangolinDomains, PangolinDomain } from 'services/pangolin/pangolin_domain';

import { IssuesService } from './issues_service';
import { HistoryService } from './history_service';
import { ThroughputService } from './throughput_service';

import { HostsService } from './hosts_service';
import { SchedulesService } from './schedule_service';
import { CountersService } from './counters_service';
import { UpdateUpgradeService } from './update_upgrade_service';
import { VPNClientService } from './vpn_clients_service';
import { VPNServerService } from './vpn_server_service';
import { UpdateHistoryService } from './update_history_service';
import { UpdateReleaseService } from './update_release_service';
import { UpdateRestoreService } from './update_restore_service';
import { UpdateSnapshotService } from './update_snapshot_service';

export type LoaderContext = { fcm: FCMMessage, url?: never } | { url: URL, fcm?: never };

export function loadAllPangolinDomains(context?: LoaderContext): Promise<boolean> {
  // Mark all the old domains for removal
  const removable: string[] = [];
  GlobalPangolinDomains.domains.forEach((domain) => {
    if (!GlobalAccountsDomain.pangolins.has(domain.id)) {
      removable.push(domain.id);
    }
  });

  // Load all the domains
  const allPromises: Promise<unknown>[] = [];
  GlobalAccountsDomain.pangolins.forEach((pangolin) => {
    // Get or create the pangolin domain
    const domain = GlobalPangolinDomains.domains.get(pangolin.id) ?? new PangolinDomain(pangolin.id);

    // Fetch the data and aggregate all the promises
    const domainPromises = loadPangolinDomain(pangolin, domain, context);
    allPromises.push(...domainPromises);

    // If the domain was newly created, add it to the set after it loads
    if (GlobalPangolinDomains.domains.get(domain.id) == null) {
      allSettled(domainPromises).then(() => {
        runInAction(() => {
          GlobalPangolinDomains.domains.set(domain.id, domain);
        });
      });
    }
  });

  // Remove all the removables
  removable.forEach((id) => {
    GlobalPangolinDomains.domains.delete(id);
  });

  return allSettled(allPromises).then(() => true);
}

export function loadPangolinDomain(pangolin: Pangolin, domain: PangolinDomain, context?: LoaderContext): Promise<unknown>[] {
  IssuesService.clearAll(domain);
  HistoryService.clearAll(domain);
  ThroughputService.clearAll(domain);

  const promises = [];

  promises.push(UpdateReleaseService.fetchAll(domain));
  promises.push(UpdateSnapshotService.fetchAll(domain));
  promises.push(UpdateHistoryService.fetchAll(domain));
  promises.push(UpdateUpgradeService.fetchActive(pangolin, domain));
  promises.push(UpdateRestoreService.fetchActive(pangolin, domain));

  if (pangolin.onlineStatus) {
    promises.push(HostsService.fetchAll(domain));
    promises.push(SchedulesService.fetchAll(domain));
    promises.push(VPNServerService.fetch(domain));
    promises.push(VPNClientService.fetchAll(domain));
    promises.push(CountersService.fetchAll(domain));

    let loadedItemIssue = false;
    let loadedItemHistory = false;

    if (context?.fcm && !context.url) {
      switch (context.fcm.kind) {
        // view issue item
        case FCMMessageKind.NEW_INCIDENT:
          if (!loadedItemIssue && context.fcm.pangolinID === pangolin.id) {
            promises.push(IssuesService.fetchItem(domain, context.fcm.issueID));
            loadedItemIssue = true;
          }
          break;
        default:
          break;
      }
    }

    if (context?.url && !context.fcm) {
      // view issue item
      const viewItemIssueMatch = matchPath<{itemID: string}>(
        context.url.pathname, {
          path: pagePathToPathString({
            kind: BasePath.LIST_ISSUES,
            pangolinID: pangolin.id,
            itemsPath: {
              kind: BasePathItems.VIEW_ITEM,
              itemID: ':itemID',
            },
          }),
        }
      );
      if (viewItemIssueMatch) {
        const { itemID } = viewItemIssueMatch.params;
        if (!loadedItemIssue && itemID != null) {
          promises.push(IssuesService.fetchItem(domain, itemID));
          loadedItemIssue = true;
        }
      }

      // view history item
      const viewItemHistoryMatch = matchPath<{itemID: string}>(
        context.url.pathname, {
          path: pagePathToPathString({
            kind: BasePath.HISTORY_LIST,
            pangolinID: pangolin.id,
            itemsPath: {
              kind: BasePathItems.VIEW_ITEM,
              itemID: ':itemID',
            },
          }),
        }
      );
      if (viewItemHistoryMatch) {
        const { itemID } = viewItemHistoryMatch.params;
        if (!loadedItemHistory && itemID != null) {
          promises.push(HistoryService.fetchItem(domain, pangolin, itemID));
          loadedItemHistory = true;
        }
      }
    }
  }

  return promises;
}

export function createPangolinDomain(pangolin: Pangolin, id: string) {
  const domain = GlobalPangolinDomains.domains.get(id) ?? new PangolinDomain(id);
  if (GlobalPangolinDomains.domains.get(domain.id) == null) {
    runInAction(() => {
      GlobalPangolinDomains.domains.set(domain.id, domain);
    });
  }
  return loadPangolinDomain(pangolin, domain);
}

const settlePromise = (p: Promise<unknown>) => p.then(x => x, e => e);
function allSettled(promises: Promise<unknown>[]): Promise<unknown> {
  return Promise.all(promises.map(settlePromise));
}
